"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowUpRight } from "@/components/icons";
import { SectionHeader } from "@/components/ui";
import { ROUTES } from "@/lib/constants";
import { COMPARISON_ROWS } from "./data";
import { NODEMAVEN_PRICING_URL, PRICING_VERIFIED_DATE } from "./pricing-data";
import ComparisonCell from "@/components/sections/alternatives/shared/comparison-cell";

const MotionDiv = motion.div;

export default function NodemavenComparisonTable() {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section
      className="full-bleed band-wash bg-band py-section border-border border-b"
      aria-labelledby="comparison-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55 }}
          className="mb-14 max-w-2xl"
        >
          <SectionHeader
            eyebrow="Feature by Feature"
            id="comparison-heading"
            heading="Proxidize vs NodeMaven at a glance."
            body="Every claim below comes from each provider's public pages. Where a provider doesn't publish something, we say so instead of guessing."
          />
        </MotionDiv>

        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55, delay: 0.08 }}
          className="border-border bg-card overflow-x-auto rounded-2xl border md:overflow-x-visible"
        >
          {/* Sticky header needs overflow-x: visible, so horizontal scroll is mobile-only —
              the table fits the container from md up. */}
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <caption className="sr-only">
              Feature comparison between Proxidize and NodeMaven mobile and residential proxies
            </caption>
            <thead>
              <tr className="border-border bg-card border-b [--th-tint:color-mix(in_srgb,var(--brand-green)_7%,var(--card))]">
                <th
                  scope="col"
                  className="text-muted-foreground z-10 bg-[--th-tint] px-5 py-4 text-left font-medium shadow-[0_1px_0_var(--border)] md:sticky md:top-[var(--header-height)]"
                >
                  Feature
                </th>
                <th
                  scope="col"
                  className="z-10 px-5 py-4 text-left font-semibold shadow-[0_1px_0_var(--border)] md:sticky md:top-[var(--header-height)]"
                  style={{
                    color: "var(--brand-green)",
                    backgroundColor: "color-mix(in srgb, var(--brand-green) 10%, var(--card))",
                  }}
                >
                  Proxidize
                </th>
                <th
                  scope="col"
                  className="text-foreground z-10 bg-[--th-tint] px-5 py-4 text-left font-semibold shadow-[0_1px_0_var(--border)] md:sticky md:top-[var(--header-height)]"
                >
                  NodeMaven
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => (
                <tr key={row.feature} className="border-border border-b last:border-b-0">
                  <th
                    scope="row"
                    className="text-foreground px-5 py-4 text-left align-top font-medium"
                  >
                    {row.feature}
                  </th>
                  <td
                    className="text-muted-foreground px-5 py-4 align-top leading-relaxed"
                    style={{
                      backgroundColor: "color-mix(in srgb, var(--brand-green) 4%, transparent)",
                    }}
                  >
                    <ComparisonCell value={row.proxidize} />
                  </td>
                  <td className="text-muted-foreground px-5 py-4 align-top leading-relaxed">
                    <ComparisonCell value={row.nodemaven} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </MotionDiv>

        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Link
            href={ROUTES.pricingMobileProxies}
            className="text-brand hover:text-brand/80 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
          >
            See Proxidize mobile proxy pricing
            <span aria-hidden="true">→</span>
          </Link>
          <a
            href={NODEMAVEN_PRICING_URL}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
          >
            See NodeMaven pricing
            <ArrowUpRight size={14} aria-hidden="true" />
          </a>
          <p className="text-muted-foreground w-full text-xs">
            Verified {PRICING_VERIFIED_DATE}. Both providers change pricing and features, so check
            the live pages before deciding. NodeMaven prices one combined residential+mobile pool —
            it does not publish separate residential and mobile rates.
          </p>
        </div>
      </div>
    </section>
  );
}
